import {
  type QueryKey,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';

import type { DisablerEvent, VehicleGroup } from '@cobliteam/common-types';

import {
  type DeviceStatus,
  DeviceStatusRequester,
  DisablerRequester,
  GraphQLVehicleGroupsRequester,
  type UserPreferences,
} from '@cobliteam/api-lib';
import type { DeviceFragment } from '@cobliteam/api-lib/src/__generated__/graphql';
import type { DeviceStatusEvent } from '@cobliteam/api-lib/src/dashboard/device/definitions';
import GraphQLDeviceRequester from '@cobliteam/api-lib/src/dashboard/device/GraphQLDeviceRequester';

import { Environment } from '@cobliteam/react-environments';

import useUser, { QUERY_KEY as USER_QUERY_KEY } from '../useUser';

const env = Environment.getInstance().getEnvironment();
const grahpQLDeviceRequester = new GraphQLDeviceRequester();
const graphQLVehicleGroupsRequester = new GraphQLVehicleGroupsRequester();
const disablerRequester = new DisablerRequester({ host: env.herbieApiHost });
const deviceStatusRequester = new DeviceStatusRequester({ host: env.apiHost });

const QUERY_KEY = 'DEVICES';

interface UseGetDevicesProps {
  vehicleGroupIds?: string[];
}

const useGetDevices = (
  { vehicleGroupIds }: UseGetDevicesProps,
  options?: UseQueryOptions<DeviceFragment[]>
) => {
  const groupIds = vehicleGroupIds?.join(',');
  const queryKey = groupIds ? [QUERY_KEY, groupIds] : [QUERY_KEY];

  return useQuery<DeviceFragment[]>({
    queryKey,
    queryFn: async () => {
      const devices = await grahpQLDeviceRequester.getDevices({
        vehicleGroupIds,
      });
      return devices;
    },
    ...options,
  });
};

const useGetDisabledDevices = (options?: UseQueryOptions<DisablerEvent[]>) => {
  return useQuery<DisablerEvent[]>({
    queryKey: [QUERY_KEY, 'FLEET_STATUS'],
    queryFn: () => disablerRequester.getFleetDevicesStatus(),
    refetchOnMount: false,
    ...options,
  });
};

const useGetDevicesGroup = (options?: UseQueryOptions<VehicleGroup[]>) => {
  return useQuery<VehicleGroup[]>({
    queryKey: [QUERY_KEY, 'GROUP'],
    queryFn: () => graphQLVehicleGroupsRequester.getVehicleGroups(),
    refetchOnMount: false,
    ...options,
  });
};

const usePrefetchDevices = ({ enabled = false }: { enabled: boolean }) => {
  const queryClient = useQueryClient();
  const { getUser, getSession } = useUser();

  if (!enabled) return null;

  const fleetId = getSession().fleet.id;
  const preferencies: UserPreferences | undefined = queryClient.getQueryData([
    USER_QUERY_KEY,
    getUser().id,
  ]);
  const vehicleGroupIds = preferencies?.vehicleGroupIds;
  const groupIds = vehicleGroupIds?.join(',');
  const queryKey = groupIds ? [QUERY_KEY, groupIds] : [QUERY_KEY];

  queryClient.prefetchQuery([QUERY_KEY, 'GROUP', fleetId], () =>
    graphQLVehicleGroupsRequester.getVehicleGroups()
  );

  queryClient.prefetchQuery(queryKey, async () => {
    const devices = await grahpQLDeviceRequester.getDevices({
      vehicleGroupIds,
    });
    return devices;
  });

  return true;
};

const useGetQueryDataDevices = () => {
  const queryClient = useQueryClient();
  const getData = ({ queryKey }: { queryKey: QueryKey }) => {
    return queryClient.getQueryData(queryKey);
  };
  return getData;
};

const useSetQueryDataDevices = () => {
  const queryClient = useQueryClient();
  const updateData = ({
    queryKey,
    data,
  }: {
    queryKey: QueryKey;
    data: DeviceStatusEvent[];
  }) => {
    return queryClient.setQueryData<DeviceFragment[]>(
      queryKey,
      (devicesVehicle) => {
        if (!devicesVehicle) return devicesVehicle;

        let devices: DeviceFragment[] = devicesVehicle;

        data.forEach((deviceStatusEvent) => {
          const { deviceId, ...lastLocation } = deviceStatusEvent;
          const deviceIndex = devices.findIndex(
            (device) => device.id === deviceId
          );

          if (deviceIndex >= 0) {
            const foundDevice = devices[deviceIndex];
            if (
              !foundDevice.lastLocation ||
              (foundDevice.lastLocation.timeInMillis &&
                lastLocation.date > foundDevice.lastLocation.timeInMillis)
            ) {
              const updateDevice: DeviceFragment = {
                ...foundDevice,
                lastLocation: {
                  latitude: lastLocation.coordinate.latitude,
                  longitude: lastLocation.coordinate.longitude,
                  timeInMillis: lastLocation.date,
                  heading: lastLocation.heading,
                  ignitionOn: lastLocation.ignitionOn,
                  speed: lastLocation.speed,
                  idleOffDurationInMillis:
                    lastLocation.idleOffDurationInMillis ||
                    foundDevice.lastLocation?.idleOffDurationInMillis,
                  idleOnDurationInMillis:
                    lastLocation.idleOnDurationInMillis ||
                    foundDevice.lastLocation?.idleOnDurationInMillis,
                  cobliIngestedAt:
                    lastLocation.ingestionTimeInMillis ||
                    foundDevice.lastLocation?.cobliIngestedAt,
                  isPlugged:
                    lastLocation.isPlugged ||
                    foundDevice.lastLocation?.isPlugged,
                  pluggedUpdateTimestamp:
                    lastLocation.pluggedUpdateTimestamp ||
                    foundDevice.lastLocation?.pluggedUpdateTimestamp,
                },
              };

              devices = [
                ...devices.slice(0, deviceIndex),
                updateDevice,
                ...devices.slice(deviceIndex + 1),
              ];
            }
          }
        });

        return devices;
      }
    );
  };

  return updateData;
};

const useGetDeviceStatus = () =>
  useQuery<DeviceStatus[]>({
    queryKey: [QUERY_KEY, 'STATUS'],
    queryFn: () => deviceStatusRequester.getDevicesStatus(),
    refetchOnMount: false,
  });

export { QUERY_KEY };
export {
  useGetDevices,
  useGetDisabledDevices,
  useGetDevicesGroup,
  usePrefetchDevices,
  useGetQueryDataDevices,
  useSetQueryDataDevices,
  useGetDeviceStatus,
};
